import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ServiceAreas = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [cities, setCities] = useState([]);
  const [services, setServices] = useState([]);
  const [pages, setPages] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedService, setSelectedService] = useState('');
  const [message, setMessage] = useState('');

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('adminToken')}`,
    'Content-Type': 'application/json'
  });

  const fetchStats = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/service-areas/stats`);
      if (res.ok) setStats(await res.json());
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, []);

  const fetchCities = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/service-areas/cities`);
      if (res.ok) {
        const data = await res.json();
        setCities(data.cities || []);
      }
    } catch (err) {
      console.error('Error fetching cities:', err);
    }
  }, []);

  const fetchServices = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/service-areas/services`);
      if (res.ok) {
        const data = await res.json();
        setServices(data.services || []);
      }
    } catch (err) {
      console.error('Error fetching services:', err);
    }
  }, []);

  const fetchPages = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/service-areas/pages?limit=200`);
      if (res.ok) {
        const data = await res.json();
        setPages(data.pages || []);
      }
    } catch (err) {
      console.error('Error fetching pages:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchStats();
    fetchCities();
    fetchServices();
    fetchPages();
  }, [fetchStats, fetchCities, fetchServices, fetchPages]);

  const generateSinglePage = async () => {
    if (!selectedCity || !selectedService) {
      setMessage('Please select both a city and service');
      return;
    }
    setGenerating(true);
    setMessage('');
    try {
      const res = await fetch(
        `${API_URL}/api/service-areas/generate/${selectedService}/${selectedCity}`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      const data = await res.json();
      setMessage(data.message || 'Page generated!');
      fetchStats();
      fetchPages();
    } catch (err) {
      setMessage('Error generating page');
    }
    setGenerating(false);
  };

  const generateAllForService = async (serviceSlug) => {
    setGenerating(true);
    setMessage('');
    try {
      const res = await fetch(
        `${API_URL}/api/service-areas/generate-all/${serviceSlug}`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      const data = await res.json();
      setMessage(data.message || 'Generation started!');
      setTimeout(() => { fetchStats(); fetchPages(); }, 2000);
    } catch (err) {
      setMessage('Error generating pages');
    }
    setGenerating(false);
  };

  const generateAllPages = async () => {
    setGenerating(true);
    setMessage('');
    try {
      const res = await fetch(
        `${API_URL}/api/service-areas/generate-all-pages`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      const data = await res.json();
      setMessage(data.message || 'Generation started!');
      setTimeout(() => { fetchStats(); fetchPages(); }, 3000);
    } catch (err) {
      setMessage('Error generating all pages');
    }
    setGenerating(false);
  };

  const publishAllPages = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/service-areas/publish-all`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      const data = await res.json();
      setMessage(data.message || 'Pages published!');
      fetchStats();
      fetchPages();
    } catch (err) {
      setMessage('Error publishing pages');
    }
    setLoading(false);
  };

  const publishPage = async (pageId) => {
    try {
      await fetch(`${API_URL}/api/service-areas/pages/${pageId}/publish`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      fetchStats();
      fetchPages();
    } catch (err) {
      console.error('Error publishing page:', err);
    }
  };

  const deletePage = async (pageId) => {
    if (!window.confirm('Delete this page?')) return;
    try {
      await fetch(`${API_URL}/api/service-areas/pages/${pageId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      fetchPages();
      fetchStats();
    } catch (err) {
      console.error('Error deleting page:', err);
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'cities', label: 'Cities (32)', icon: '🏙️' },
    { id: 'services', label: 'Services', icon: '🦷' },
    { id: 'pages', label: 'Generated Pages', icon: '📄' },
    { id: 'generate', label: 'Generate', icon: '⚡' },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>Service Area Pages | Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white px-6 py-4">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold">🗺️ Service Area Pages</h1>
            <p className="text-teal-100 text-sm">Generate SEO landing pages for surrounding cities</p>
          </div>
          <button
            onClick={generateAllPages}
            disabled={generating}
            className="bg-white text-teal-600 px-4 py-2 rounded-lg font-semibold hover:bg-gray-100 disabled:opacity-50"
          >
            {generating ? '⏳ Generating...' : '🚀 Generate All Pages'}
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className="mx-6 mt-4 p-3 bg-teal-50 border border-teal-200 rounded-lg text-teal-700">
          {message}
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 px-6 bg-white">
        <nav className="flex space-x-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition ${
                activeTab === tab.id
                  ? 'border-teal-500 text-teal-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.icon} {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="p-6">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-6">
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-teal-500">
                <p className="text-gray-500 text-sm">Total Pages</p>
                <p className="text-2xl font-bold text-gray-800">{stats?.total_pages || 0}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
                <p className="text-gray-500 text-sm">Published</p>
                <p className="text-2xl font-bold text-green-600">{stats?.published || 0}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
                <p className="text-gray-500 text-sm">Drafts</p>
                <p className="text-2xl font-bold text-yellow-600">{stats?.draft || 0}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
                <p className="text-gray-500 text-sm">Cities</p>
                <p className="text-2xl font-bold text-blue-600">{stats?.cities_covered || 0} / {cities.length}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-purple-500">
                <p className="text-gray-500 text-sm">Services</p>
                <p className="text-2xl font-bold text-purple-600">{stats?.services_covered || 0} / {services.length}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow border-l-4 border-pink-500">
                <p className="text-gray-500 text-sm">Coverage</p>
                <p className="text-2xl font-bold text-pink-600">{stats?.coverage_percentage || 0}%</p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h3 className="font-semibold text-gray-800 mb-4">Quick Actions</h3>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={generateAllPages}
                  disabled={generating}
                  className="bg-teal-600 text-white px-4 py-2 rounded hover:bg-teal-700 disabled:opacity-50"
                >
                  {generating ? '⏳' : '⚡'} Generate All {stats?.potential_pages || (cities.length * services.length)} Pages
                </button>
                <button
                  onClick={publishAllPages}
                  disabled={loading || stats?.draft === 0}
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
                >
                  ✅ Publish All Drafts ({stats?.draft || 0})
                </button>
                <button
                  onClick={() => fetchPages()}
                  className="bg-gray-100 text-gray-700 px-4 py-2 rounded hover:bg-gray-200"
                >
                  🔄 Refresh
                </button>
              </div>
            </div>

            {/* Recent Pages */}
            <div className="bg-white rounded-lg shadow">
              <div className="p-4 border-b">
                <h3 className="font-semibold text-gray-800">Recent Generated Pages</h3>
              </div>
              <div className="divide-y max-h-96 overflow-y-auto">
                {pages.slice(0, 10).map(page => (
                  <div key={page.id} className="p-4 flex justify-between items-center hover:bg-gray-50">
                    <div>
                      <p className="font-medium text-gray-800">{page.service} - {page.city}</p>
                      <p className="text-sm text-gray-500">/{page.full_slug}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-xs rounded ${
                        page.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {page.status}
                      </span>
                      {page.status === 'draft' && (
                        <button
                          onClick={() => publishPage(page.id)}
                          className="text-teal-600 hover:text-teal-800 text-sm"
                        >
                          Publish
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {pages.length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    No pages generated yet. Click "Generate All Pages" to start!
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Cities Tab */}
        {activeTab === 'cities' && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h3 className="font-semibold text-gray-800">Cities Within 25 Miles of 76132</h3>
              <p className="text-sm text-gray-500">Sorted by distance from Fort Worth</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
              {cities.sort((a, b) => a.distance - b.distance).map(city => (
                <div key={city.slug} className="border rounded-lg p-4 hover:shadow-md transition">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold text-gray-800">{city.name}</h4>
                      <p className="text-sm text-gray-500">{city.county} County</p>
                    </div>
                    <span className="bg-teal-100 text-teal-700 px-2 py-1 rounded text-sm">
                      {city.distance} mi
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">Pop: {city.population?.toLocaleString()}</p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {city.nearby_landmarks?.slice(0, 2).map((l, i) => (
                      <span key={i} className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded">
                        {l}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Services Tab */}
        {activeTab === 'services' && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b">
              <h3 className="font-semibold text-gray-800">Dental Services for Location Pages</h3>
            </div>
            <div className="divide-y">
              {services.map(service => (
                <div key={service.slug} className="p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div>
                      <h4 className="font-semibold text-gray-800">{service.service}</h4>
                      <p className="text-sm text-gray-600 mt-1">{service.description}</p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {service.keywords?.slice(0, 4).map((kw, i) => (
                          <span key={i} className="bg-teal-50 text-teal-600 text-xs px-2 py-0.5 rounded">
                            {kw}
                          </span>
                        ))}
                      </div>
                    </div>
                    <button
                      onClick={() => generateAllForService(service.slug)}
                      disabled={generating}
                      className="bg-teal-100 text-teal-700 px-3 py-1 rounded text-sm hover:bg-teal-200 disabled:opacity-50"
                    >
                      Generate All Cities
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 mt-2 italic">{service.doctor_highlight}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pages Tab */}
        {activeTab === 'pages' && (
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b flex justify-between items-center">
              <div>
                <h3 className="font-semibold text-gray-800">Generated Location Pages ({pages.length})</h3>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={publishAllPages}
                  disabled={loading}
                  className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 disabled:opacity-50"
                >
                  Publish All Drafts
                </button>
              </div>
            </div>
            {loading ? (
              <div className="p-8 text-center text-gray-500">Loading...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">City</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Distance</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">URL</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {pages.map(page => (
                      <tr key={page.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-800">{page.service}</td>
                        <td className="px-4 py-3 text-gray-600">{page.city}</td>
                        <td className="px-4 py-3 text-gray-500">{page.distance_miles} mi</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs rounded ${
                            page.status === 'published' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                          }`}>
                            {page.status}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <a href={`/${page.full_slug}`} target="_blank" rel="noreferrer" className="text-teal-600 hover:underline text-sm">
                            /{page.full_slug}
                          </a>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            {page.status === 'draft' && (
                              <button
                                onClick={() => publishPage(page.id)}
                                className="text-green-600 hover:text-green-800 text-sm"
                              >
                                Publish
                              </button>
                            )}
                            <button
                              onClick={() => deletePage(page.id)}
                              className="text-red-600 hover:text-red-800 text-sm"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {pages.length === 0 && (
                  <div className="p-8 text-center text-gray-500">
                    No pages generated yet.
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Generate Tab */}
        {activeTab === 'generate' && (
          <div className="max-w-2xl">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold text-gray-800 mb-4">Generate Single Page</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Service</label>
                  <select
                    value={selectedService}
                    onChange={(e) => setSelectedService(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="">Select a service...</option>
                    {services.map(s => (
                      <option key={s.slug} value={s.slug}>{s.service}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <select
                    value={selectedCity}
                    onChange={(e) => setSelectedCity(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2"
                  >
                    <option value="">Select a city...</option>
                    {cities.sort((a, b) => a.distance - b.distance).map(c => (
                      <option key={c.slug} value={c.slug}>{c.name} ({c.distance} mi)</option>
                    ))}
                  </select>
                </div>
                <button
                  onClick={generateSinglePage}
                  disabled={generating || !selectedCity || !selectedService}
                  className="w-full bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700 disabled:opacity-50"
                >
                  {generating ? '⏳ Generating...' : '⚡ Generate Page'}
                </button>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6 mt-6">
              <h3 className="font-semibold text-gray-800 mb-4">Bulk Generation</h3>
              <div className="space-y-3">
                <button
                  onClick={generateAllPages}
                  disabled={generating}
                  className="w-full bg-gradient-to-r from-teal-600 to-teal-700 text-white px-4 py-3 rounded-lg hover:from-teal-700 hover:to-teal-800 disabled:opacity-50 font-semibold"
                >
                  {generating ? '⏳ Generating...' : `🚀 Generate ALL ${cities.length * services.length} Pages`}
                </button>
                <p className="text-sm text-gray-500 text-center">
                  This will create {cities.length} city pages × {services.length} services = {cities.length * services.length} total pages
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ServiceAreas;
